import {Box, Button} from '@material-ui/core';
import moment from 'moment';
import React, {PureComponent} from 'react';
import {connect} from 'react-redux';
import {Dispatch} from 'redux';
import {FileParameter} from '../../../../api/api';
import {
  calibrateDevice,
  saveCalibrationFactor,
  saveLabMeasurement,
  saveSpectralMeasurement,
  saveSpectralMeasurementDate,
  saveStationId,
  uploadDeviceCSV,
} from '../../../../state/ducks/devices/operations';
import {localized} from '../../../../state/i18n/i18n';
import {showError, showSuccess} from '../../../../state/snackbar/operations';
import {AppState} from '../../../../state/store';
import {GetColorByIndex} from '../../../../utilities/colors';
import {DateFormatWithHours} from '../../../../utilities/constants';
import {ColorRouter, ColorSquare} from '../color-square/color-square';
import DeviceDateTimePicker from '../device-management-datetimepicker/device-management-datetimepicker-class';
import DeviceManagementField from '../device-management-field/device-management-field';
import DeviceManagementFieldString from '../device-management-field/device-management-field-string';
import DevicePopUp from '../device-pop-up.tsx/device-pop-up';
import './device-management-container.scss';

interface PropsFromParents {
  deviceId?: string;
  index: number;
}

interface State {
  timeOfMeasurement?: Date | null;
  popUp: JSX.Element | undefined;
}

const mapStateToProps = (state: AppState, props: PropsFromParents) => {
  return {
    pendingStatus: state.notificationSettingsReducer.pending,
    device: state.devicesReducer.devices.find((device) => device.deviceId === props.deviceId),
  };
};

const mapDispatchToProps = (dispatch: Dispatch) => ({
  saveCalibrationFactor: async (calibrationFactor: number, deviceId: string) =>
    (await saveCalibrationFactor(calibrationFactor, deviceId))(dispatch),
  uploadCsv: async (deviceId: string, file: FileParameter) => (await uploadDeviceCSV(deviceId, file))(dispatch),

  saveSpectralMeasurement: async (spectralMeasurement: number, deviceId: string) =>
    (await saveSpectralMeasurement(spectralMeasurement, deviceId))(dispatch),
  saveLabMeasurement: async (labMeasurement: number, deviceId: string) =>
    (await saveLabMeasurement(labMeasurement, deviceId))(dispatch),
  saveStationId: async (stationId: string, deviceId: string) => (await saveStationId(stationId, deviceId))(dispatch),
  getNewCalibrationFactor: async (timeOfMeasurement: Date, deviceId: string) =>
    (await calibrateDevice(deviceId, timeOfMeasurement))(dispatch),
  saveSpectralMeasurementDate: async (spectralMeasurementDateTime: Date | undefined, deviceId: string) =>
    (await saveSpectralMeasurementDate(spectralMeasurementDateTime, deviceId))(dispatch),
  getSnackbarError: () => showError(localized('DataNotAvailable'))(dispatch),
  getSnackbarSuccess: () => showSuccess(localized('CalibrationIsUpdated'))(dispatch),
  getSnackbarSuccessGenerel: () => showSuccess(localized('YourChangesAreSaved'))(dispatch),
  getSnackbarErrorGenerel: (error: string) => showError(localized('Error') + ': ' + error)(dispatch),
});
type Props = Partial<ReturnType<typeof mapStateToProps>> &
  Partial<ReturnType<typeof mapDispatchToProps>> &
  PropsFromParents;

class DeviceManagementContainer extends PureComponent<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {popUp: undefined, timeOfMeasurement: this.props.device?.spectralMeasurementDateTime};
  }

  componentDidUpdate = (prevProps: Props) => {
    if (prevProps.device !== this.props.device) {
      this.setState({timeOfMeasurement: this.props.device?.spectralMeasurementDateTime});
    }
  };

  private saveCalibration = async (value: number) => {
    this.props.saveCalibrationFactor && (await this.props.saveCalibrationFactor(value, this.props.deviceId!));
  };

  private saveSpectralMeasure = async (value: number) => {
    this.props.saveSpectralMeasurement && (await this.props.saveSpectralMeasurement(value, this.props.deviceId!));
    this.props.saveSpectralMeasurementDate && this.props.saveSpectralMeasurementDate(undefined, this.props.deviceId!);
    this.setState({timeOfMeasurement: null});
  };

  private saveLabMeasurement = async (value: number) => {
    this.props.saveLabMeasurement && (await this.props.saveLabMeasurement(value, this.props.deviceId!));
  };

  private saveStationId = async (value: string) => {
    this.props.saveStationId && (await this.props.saveStationId(value, this.props.deviceId!));
  };

  private calibrationFactor = () => {
    return (
      <div className="inputMarginRightSide">
        <div className="boldTextHeaders">{localized('CalibrationFactor')}</div>
        <DeviceManagementField initialValue={this.props.device?.calibrationFactor} onChange={this.saveCalibration} />
      </div>
    );
  };

  private spectralCallback = (dataFromDateTimePicker: Date) => {
    this.props.saveSpectralMeasurementDate &&
      this.props.saveSpectralMeasurementDate(dataFromDateTimePicker, this.props.deviceId!);
    this.props.getSnackbarSuccessGenerel && this.props.getSnackbarSuccessGenerel();
    this.setState({
      timeOfMeasurement: dataFromDateTimePicker,
      popUp: <DevicePopUp callbackFromParent={this.popUpCallback} />,
    });
  };

  private popUpCallback = (accept: boolean) => {
    this.setState({popUp: undefined});
    if (accept === true && this.state.timeOfMeasurement) {
      this.calculateCalibration(this.state.timeOfMeasurement);
    }
  };
  private uploadFileCsv = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      const file = event.target.files[0];
      const formData = {
        data: file,
        fileName: file.name,
      };

      try {
        if (!this.props.uploadCsv) return;
        this.props.uploadCsv(this.props.deviceId!, formData);

        if (!this.props.getSnackbarSuccessGenerel) return;
        this.props.getSnackbarSuccessGenerel();
      } catch (error) {}
    }
  };

  private spectralMeasurement = (deviceId: string | undefined) => {
    const buttonId = 'contained-button-file' + deviceId;
    return (
      <div className="inputMargin">
        <div className="boldTextHeaders">{localized('SpectralMeasurement')}</div>
        <DeviceManagementField
          initialValue={this.props.device?.spectralMeasurement!}
          onChange={this.saveSpectralMeasure}></DeviceManagementField>
        <div className="DateTimePicker">
          <DeviceDateTimePicker
            callbackFromParent={this.spectralCallback}
            selectedDateFromParent={this.props.device!.spectralMeasurementDateTime}
          />
        </div>
        <div>
          <input hidden type="file" accept=".csv" id={buttonId} onChange={this.uploadFileCsv} multiple={false} />
          <label htmlFor={buttonId}>
            <Button variant="contained" color="primary" component="span">
              {localized('UploadCsv')}
            </Button>
          </label>
        </div>
      </div>
    );
  };

  private labMeasurement = () => {
    return (
      <div className="inputMarginRightSide">
        <div className="boldTextHeaders">{localized('LabMeasurement')}</div>
        <DeviceManagementField
          initialValue={this.props.device?.labMeasurement!}
          onChange={this.saveLabMeasurement}></DeviceManagementField>
      </div>
    );
  };

  private lastChanged = () => {
    if (this.props.device?.lastChanged !== null && this.props.device?.lastChanged !== undefined) {
      return (
        <div className="LastChangedText">
          <div> {localized('LastChangedDate')}</div>
          {moment(this.props.device?.lastChanged).format(DateFormatWithHours)}
        </div>
      );
    }
  };

  private calculateCalibration = async (dataFromDateTimePicker: Date) => {
    if (dataFromDateTimePicker) {
      this.props.getNewCalibrationFactor &&
        (await this.props.getNewCalibrationFactor(dataFromDateTimePicker, this.props.deviceId!));
    }
  };

  private stationId = () => {
    return (
      <div className="inputMargin">
        <div className="boldTextHeaders">{localized('StationId')}</div>

        <DeviceManagementFieldString
          initialValue={this.props.device?.stationId ? this.props.device?.stationId : ''}
          onChange={this.saveStationId}
        />
      </div>
    );
  };

  public render() {
    return (
      <Box className="deviceManagementContainer" display="flex" flexDirection="row">
        {this.state.popUp}
        {/* Mobile Display */}
        <Box display="flex" flexDirection="column" className="mobile colorRouterDeviceManagement">
          <Box display="flex" flexDirection="row" className="deviceItemHeader">
            <Box display="flex" flexDirection="row" flex="1">
              <ColorRouter key={this.props.deviceId} color={GetColorByIndex(this.props.index)} />
              <Box flexDirection="row" display="flex" className="deviceIdMargin">
                {this.props.deviceId}
              </Box>
            </Box>
            <Box className="lastChanged">{this.lastChanged()}</Box>
          </Box>
          <Box flexDirection="row" display="flex">
            <Box className="rightBorder">{this.spectralMeasurement(this.props.deviceId)}</Box>
            <Box className="leftBorder">
              {this.labMeasurement()}
              {this.calibrationFactor()}
              {this.stationId()}
            </Box>
          </Box>
        </Box>

        {/* Desktop Display */}
        <Box display="flex" className="desktop" flexDirection="column" flex="1">
          <Box display="flex" flexDirection="row" className="deviceItemHeader" flex="1">
            <Box flexDirection="row" display="flex" flex="1">
              <ColorSquare key={this.props.deviceId} color={GetColorByIndex(this.props.index)} />
              <Box flexDirection="row" display="flex" className="deviceIdMargin">
                {this.props.deviceId}
              </Box>
            </Box>
            <Box className="lastChanged">{this.lastChanged()}</Box>
          </Box>
          <Box flexDirection="row" display="flex">
            <Box className="rightBorder">{this.spectralMeasurement(this.props.deviceId)}</Box>
            <Box className="leftBorder">
              {this.labMeasurement()}
              {this.calibrationFactor()}
              {this.stationId()}
            </Box>
          </Box>
        </Box>
      </Box>
    );
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(DeviceManagementContainer);
